import { match } from 'ts-pattern';
import { RegularMetricId } from '../graphql/generated/graphql-sdk';
import { KpiFactboxSingleMetricVizViews } from '../v2/common/components/data-viz-containers/kpi-factbox-single-metric/types';
import { OverTimeUnsegmentedVizViews } from '../v2/common/components/data-viz-containers/over-time-unsegmented/types';
import { OverTimeSegmentedVizViews } from '../v2/common/components/data-viz-containers/overtime-segmented-query/types';
import { TimePeriodSegmentedVizViews } from '../v2/common/components/data-viz-containers/time-period-segmented/types';
import { TimePeriodUnsegmentedVizViews } from '../v2/common/components/data-viz-containers/time-period-unsegmented/types';
import {
  StoryboardComponentConfig,
  StoryboardComponentIds,
} from '../v2/common/components/storyboard-components/common/types';
import { FactboxSingleMetricStoryboardComponentConfig } from '../v2/common/components/storyboard-components/factbox-single-metric/types';
import { OverTimeSegmentedStoryboardComponentConfig } from '../v2/common/components/storyboard-components/over-time-segmented/types';
import { OverTimeUnsegmentedStoryboardComponentConfig } from '../v2/common/components/storyboard-components/over-time-unsegmented/types';
import { TimePeriodSegmentedStoryboardComponentConfig } from '../v2/common/components/storyboard-components/time-period-segmented/types';
import { TimePeriodUnsegmentedStoryboardComponentConfig } from '../v2/common/components/storyboard-components/time-period-unsegmented/types';
import {
  ChappiKpiVisualization,
  ChappiLineChartVisualization,
  ChappiOverTimeBarChartVisualization,
  ChappiPieChartVisualization,
  ChappiTableVisualization,
  ChappiTimePeriodBarChartVisualization,
  convertChappiPieTimeSelection,
  convertChappiSegmentation,
  convertChappiTimeSelection,
  VisualizationBackend,
} from './chappi_types';

const parseChappiLineChartVisualization = (
  viz: ChappiLineChartVisualization
): StoryboardComponentConfig => {
  const segmentations =
    viz.lineChartSegmentation.length > 0
      ? viz.lineChartSegmentation.map(convertChappiSegmentation)
      : undefined;

  // two cases
  // segmented or unsegmented

  let componentConfig: StoryboardComponentConfig;
  if (segmentations && segmentations.length === 1) {
    const overTimeSegmentedComponentConfig: OverTimeSegmentedStoryboardComponentConfig =
      {
        componentId: StoryboardComponentIds.OVERTIME_SEGMENTED,
        dataConfig: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: viz.lineChartMetrics[0] as unknown as RegularMetricId,
            },
          },
          timeSelection: convertChappiTimeSelection(viz.lineChartTimeSelection),
          segmentBy: segmentations[0],
        },
        selectedView: OverTimeSegmentedVizViews.LINE_CHART,
      };
    componentConfig = overTimeSegmentedComponentConfig;
  } else {
    // unsegmented case
    const overTimeUnsegmentedComponentConfig: OverTimeUnsegmentedStoryboardComponentConfig =
      {
        componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
        dataConfig: {
          queries: viz.lineChartMetrics.map((metric) => ({
            metric: {
              type: 'RegularMetricId',
              value: metric as unknown as RegularMetricId,
            },
          })),
          timeSelection: convertChappiTimeSelection(viz.lineChartTimeSelection),
        },
        selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
      };
    componentConfig = overTimeUnsegmentedComponentConfig;
  }
  return componentConfig;
};

const parseChappiPieChartVisualization = (
  viz: ChappiPieChartVisualization
): StoryboardComponentConfig => {
  const segmentation = convertChappiSegmentation(viz.pieChartSegmentation);
  const pieChartComponentConfig: TimePeriodUnsegmentedStoryboardComponentConfig =
    {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        queries: [
          {
            metric: {
              value: viz.pieChartMetric as unknown as RegularMetricId,
              type: 'RegularMetricId',
            },
          },
        ],
        filters: viz.filters?.sql,
        groupBy: segmentation,
        timeSelection: convertChappiPieTimeSelection(viz.pieChartTimeSelection),
      },
      selectedView: TimePeriodUnsegmentedVizViews.PIE_CHART,
    };
  return pieChartComponentConfig;
};

const parseChappiKpiVisualization = (
  viz: ChappiKpiVisualization
): StoryboardComponentConfig => {
  const kpiComponentConfig: FactboxSingleMetricStoryboardComponentConfig = {
    componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
    dataConfig: {
      section: {
        query: {
          metric: {
            type: 'RegularMetricId',
            value: viz.kpiMetric[0] as unknown as RegularMetricId,
          },
          timeSelection: convertChappiPieTimeSelection(viz.kpiTimeSelection),
          filters: viz.filters?.sql,
        },
      },
    },
    selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
  };
  return kpiComponentConfig;
};

const parseChappiTableVisualization = (
  viz: ChappiTableVisualization
): StoryboardComponentConfig => {
  return {
    componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
    dataConfig: {
      queries: viz.tableMetrics.map((metric) => ({
        metric: {
          type: 'RegularMetricId',
          value: metric as unknown as RegularMetricId,
        },
      })),
      timeSelection: convertChappiTimeSelection(viz.tableTimeSelection),
    },
    selectedView: OverTimeUnsegmentedVizViews.TABLE_VIEW,
  };
};

const parseChappiTimePeriodBarChartVisualization = (
  viz: ChappiTimePeriodBarChartVisualization
): StoryboardComponentConfig => {
  const segmentation =
    viz.barChartSegmentation.length > 0
      ? viz.barChartSegmentation.map(convertChappiSegmentation)
      : undefined;
  if (!segmentation || segmentation.length === 0) {
    throw new Error('Time period bar chart viz needs segmentation');
  } else if (segmentation.length === 1) {
    // unsegmented
    const timePeriodUnsegmentedComponentConfig: TimePeriodUnsegmentedStoryboardComponentConfig =
      {
        componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
        dataConfig: {
          queries: viz.barChartMetrics.map((metric) => ({
            metric: {
              type: 'RegularMetricId',
              value: metric as unknown as RegularMetricId,
            },
          })),
          timeSelection: convertChappiPieTimeSelection(
            viz.barChartTimeSelection
          ),
          groupBy: segmentation[0],
        },
        selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
      };
    return timePeriodUnsegmentedComponentConfig;
  } else {
    // segmented
    const timePeriodSegmentedComponentConfig: TimePeriodSegmentedStoryboardComponentConfig =
      {
        componentId: StoryboardComponentIds.TIME_PERIOD_SEGMENTED,
        dataConfig: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: viz.barChartMetrics[0] as unknown as RegularMetricId,
            },
            timeSelection: convertChappiPieTimeSelection(
              viz.barChartTimeSelection
            ),
            filters: viz.filters?.sql,
            groupBy: segmentation[0],
            segmentBy: segmentation[1],
          },
        },
        selectedView: TimePeriodSegmentedVizViews.BAR_CHART,
      };
    return timePeriodSegmentedComponentConfig;
  }
};

const parseChappiOverTimeBarChartVisualization = (
  viz: ChappiOverTimeBarChartVisualization
): StoryboardComponentConfig => {
  const segmentations =
    viz.barChartSegmentation.length > 0
      ? viz.barChartSegmentation.map(convertChappiSegmentation)
      : undefined;

  // two cases
  // segmented or unsegmented

  let componentConfig: StoryboardComponentConfig;
  if (segmentations && segmentations.length === 1) {
    const overTimeSegmentedComponentConfig: OverTimeSegmentedStoryboardComponentConfig =
      {
        componentId: StoryboardComponentIds.OVERTIME_SEGMENTED,
        dataConfig: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: viz.barChartMetrics[0] as unknown as RegularMetricId,
            },
            filters: viz.filters?.sql,
          },
          timeSelection: convertChappiTimeSelection(viz.barChartTimeSelection),
          segmentBy: segmentations[0],
        },
        selectedView: OverTimeSegmentedVizViews.BAR_CHART,
      };
    componentConfig = overTimeSegmentedComponentConfig;
  } else {
    // unsegmented case
    const overTimeUnsegmentedComponentConfig: OverTimeUnsegmentedStoryboardComponentConfig =
      {
        componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
        dataConfig: {
          queries: viz.barChartMetrics.map((metric) => ({
            metric: {
              type: 'RegularMetricId',
              value: metric as unknown as RegularMetricId,
            },
            filters: viz.filters?.sql,
          })),
          timeSelection: convertChappiTimeSelection(viz.barChartTimeSelection),
        },
        selectedView: OverTimeUnsegmentedVizViews.BAR_CHART,
      };
    componentConfig = overTimeUnsegmentedComponentConfig;
  }
  return componentConfig;
};

export const parseVisualization = (
  value: VisualizationBackend
): StoryboardComponentConfig => {
  return match(value)
    .with({ __typename: 'ChappiLineChartVisualization' }, (viz) =>
      parseChappiLineChartVisualization(viz)
    )
    .with({ __typename: 'ChappiPieChartVisualization' }, (viz) =>
      parseChappiPieChartVisualization(viz)
    )
    .with({ __typename: 'ChappiKpiVisualization' }, (viz) =>
      parseChappiKpiVisualization(viz)
    )
    .with({ __typename: 'ChappiTableVisualization' }, (viz) =>
      parseChappiTableVisualization(viz)
    )
    .with({ __typename: 'ChappiTimePeriodBarChartVisualization' }, (viz) =>
      parseChappiTimePeriodBarChartVisualization(viz)
    )
    .with({ __typename: 'ChappiOverTimeBarChartVisualization' }, (viz) =>
      parseChappiOverTimeBarChartVisualization(viz)
    )
    .otherwise((value) => {
      throw new Error('Invalid visualization type: ' + value.__typename);
    });
};
